// Página dedicada do detalhe do envio (rota
// /notification-center/notifications/:notificationUuid) — antes era
// um drawer; virou página para aproveitar o espaço. Visual IRMÃO da
// OrderDetailsPage: SectionCard/InfoItem (shared/detail-components),
// timeline vertical no MESMO estilo do details do Orders (conector
// por segmento + nós coloridos + cards por evento) e ações como botões
// (buttonVariants). Abas: "Detalhes" e "Template enviado". O botão
// "Voltar" restaura a busca de origem via ?from= (searchParams da
// Consulta).
import {
  ArrowLeftIcon,
  CalendarIcon,
  CheckCircle2Icon,
  ChevronRightIcon,
  ChevronsDownUpIcon,
  ChevronsUpDownIcon,
  ClockIcon,
  ExternalLinkIcon,
  ReceiptTextIcon,
  RotateCcwIcon,
  TimerIcon,
  WrenchIcon,
} from "lucide-react"
import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { Link, useSearchParams } from "react-router-dom"

import {
  InfoItem,
  SectionCard,
} from "@/components/shared/detail-components"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Badge } from "@/components/ui/badge"
import {
  Button,
  buttonVariants,
} from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { cn } from "@/lib/utils"
import type {
  NotificationDetailChannelType,
  NotificationDetailType,
} from "@/types/notification-center"
import { DateHelper } from "@/utils"

import { NOTIFICATION_MACRO_STATUS_CHART_COLORS } from "../constants"
import { useNotificationCenterNotificationDetail } from "../hooks"
import {
  formatNotificationBytes,
  formatNotificationDuration,
} from "../notification-detail-utils"
import { useResendNotification } from "../notification-mutations"
import { NotificationPayloadTree } from "./notification-payload-tree"
import {
  getOriginHref,
  NotificationChannelBadge,
  NotificationMacroStatusBadge,
} from "./notification-search-columns"
import { NotificationSentTemplates } from "./notification-sent-templates"

const detailPrefix =
  "pages.notificationCenter.notificationDetailPage"

// Id da assinatura para o link de origem: vem do payload persistido em
// dataInformationPayloadV2.billingCycle.subscription.id (o transactionId do
// scheduler, SBC-*, nem sempre casa com o id da tela de assinatura). Leitura
// null-safe sobre o payload cru (Record<string, unknown>).
const getSubscriptionIdFromPayload = (
  payload: Record<string, unknown> | undefined
): string | undefined => {
  const asRecord = (
    value: unknown
  ): Record<string, unknown> | undefined =>
    typeof value === "object" &&
    value !== null &&
    !Array.isArray(value)
      ? (value as Record<string, unknown>)
      : undefined

  const id = asRecord(
    asRecord(asRecord(payload)?.dataInformationPayloadV2)
      ?.billingCycle
  )?.subscription
  const subscriptionId = asRecord(id)?.id

  return typeof subscriptionId === "string"
    ? subscriptionId
    : undefined
}

// Href de origem: para ASSINATURA prioriza o id do payload (acima); cai para o
// href padrão (transactionId) quando ausente ou para PEDIDO.
const resolveOriginHref = (
  detail: NotificationDetailType
): string => {
  if (detail.origin.type === "SUBSCRIPTION") {
    const subscriptionId = getSubscriptionIdFromPayload(
      detail.payload
    )
    if (subscriptionId) {
      return `/subscriptions/details/${subscriptionId}`
    }
  }
  return getOriginHref(detail.origin)
}

// Ação da página como botão no padrão do details do Orders
// (Link estilizado com buttonVariants + ícone de link externo).
const ActionLinkButton = ({
  to,
  children,
}: {
  to: string
  children: React.ReactNode
}) => (
  <Link
    to={to}
    className={cn(
      buttonVariants({ variant: "outline", size: "sm" }),
      "gap-1.5"
    )}
  >
    <ExternalLinkIcon size={14} aria-hidden="true" />
    {children}
  </Link>
)

const DetailSkeleton = () => (
  <div
    data-testid="notification-detail-skeleton"
    className="space-y-3"
  >
    <Skeleton className="h-16 w-full" />
    {Array.from({ length: 4 }).map((_, index) => (
      <Skeleton
        key={`detail-skeleton-${index}`}
        className="h-24 w-full"
      />
    ))}
  </div>
)

// Timeline analítica dos NotificationTargetEvent — MESMO estilo do
// details do Orders (conector por segmento + nós coloridos + card por
// evento, mais recente primeiro), enriquecida para troubleshooting:
// tempo TOTAL criado→estado final no topo, DELTA entre etapas
// ("+584ms"), timestamps com milissegundos e os campos de `detail`
// (providerMessageId/smtpResponse/mx/worker/origin) em monospace.
const NotificationTimeline = ({
  timeline,
}: {
  timeline: NotificationDetailChannelType["timeline"]
}) => {
  const { t, i18n } = useTranslation()
  // Ordem cronológica (asc) para calcular os deltas entre etapas.
  const chronological = [...timeline].sort(
    (a, b) => a.seq - b.seq
  )
  const withDeltas = chronological.map((entry, index) => ({
    ...entry,
    deltaMs:
      index === 0
        ? null
        : new Date(entry.datetime).getTime() -
          new Date(
            chronological[index - 1].datetime
          ).getTime(),
  }))
  const lastEntry = withDeltas[withDeltas.length - 1]
  const totalMs =
    withDeltas.length > 1
      ? new Date(lastEntry.datetime).getTime() -
        new Date(withDeltas[0].datetime).getTime()
      : null
  // Exibição: mais recente primeiro (padrão Orders).
  const displayed = [...withDeltas].reverse()

  return (
    <div className="space-y-3">
      {/* Tempo total criado→estado final (ex.: "Entregue em 8,9s"). */}
      {totalMs !== null && (
        <div className="inline-flex items-center gap-2 rounded-lg border border-primary/20 bg-primary/5 px-3 py-1.5">
          <TimerIcon
            size={14}
            className="text-primary"
            aria-hidden="true"
          />
          <span className="text-sm font-medium tabular-nums text-foreground">
            {t(`${detailPrefix}.timelineTotal`, {
              status: t(
                `pages.notificationCenter.searchPage.macroStatus.${lastEntry.macroStatus}`
              ),
              duration: formatNotificationDuration(
                totalMs,
                i18n.language
              ),
            })}
          </span>
        </div>
      )}
      <div className="relative">
        <ol
          aria-label={t(`${detailPrefix}.timeline`)}
          className="space-y-0"
        >
          {displayed.map((entry, index) => {
            const isLatest = index === 0
            const isOldest = index === displayed.length - 1
            // Cor do nó via HEX inline (≠ classe Tailwind): nunca some
            // no purge/JIT — MESMO mecanismo da timeline do Orders/
            // Assinaturas (que usam style backgroundColor).
            const nodeColor =
              NOTIFICATION_MACRO_STATUS_CHART_COLORS[
                entry.macroStatus
              ]
            return (
              <li
                key={entry.seq}
                className="relative flex gap-4 pb-1"
              >
                {/* Conector até o próximo nó — segmento por etapa
                    (nunca "quebra" nem some no fim), colorido com a
                    cor do próprio nó. Omitido na etapa mais antiga.
                    Fica atrás do nó (z-10). */}
                {!isOldest && (
                  <div
                    aria-hidden="true"
                    className="absolute bottom-0 left-5 top-5 w-0.5 -translate-x-1/2 rounded-full"
                    style={{ backgroundColor: nodeColor }}
                  />
                )}
                {/* Nó da timeline. */}
                <div className="relative z-10 flex h-10 w-10 shrink-0 items-center justify-center">
                  <div
                    className="flex h-8 w-8 items-center justify-center rounded-full border-2 border-background shadow-md transition-transform hover:scale-110"
                    style={{ backgroundColor: nodeColor }}
                  >
                    {isLatest ? (
                      <CheckCircle2Icon
                        size={16}
                        className="text-white"
                      />
                    ) : (
                      <div className="h-2 w-2 rounded-full bg-white" />
                    )}
                  </div>
                </div>

                {/* Card do evento. */}
                <div
                  className={cn(
                    "group mb-4 flex-1 rounded-xl border bg-card p-4 shadow-sm transition-all hover:shadow-md",
                    isLatest &&
                      "border-primary/30 bg-gradient-to-r from-primary/5 to-transparent"
                  )}
                >
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                    <div className="space-y-1.5">
                      <span
                        className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold text-white"
                        style={{
                          backgroundColor: nodeColor,
                        }}
                      >
                        {entry.status}
                      </span>
                      {/* Campos técnicos do evento — ouro de
                          troubleshooting, monospace discreto. */}
                      {entry.detail && (
                        <dl className="space-y-0.5 font-mono text-xs text-muted-foreground">
                          {Object.entries(entry.detail).map(
                            ([key, value]) => (
                              <div
                                key={key}
                                className="flex gap-1"
                              >
                                <dt>{key}:</dt>
                                <dd className="break-all">
                                  {value}
                                </dd>
                              </div>
                            )
                          )}
                        </dl>
                      )}
                    </div>
                    <div className="flex shrink-0 items-center gap-1.5 text-xs text-muted-foreground">
                      {/* Delta vs etapa anterior. */}
                      {entry.deltaMs !== null && (
                        <span className="rounded-full bg-muted px-1.5 py-0.5 font-medium tabular-nums">
                          +
                          {formatNotificationDuration(
                            entry.deltaMs,
                            i18n.language
                          )}
                        </span>
                      )}
                      <CalendarIcon size={12} />
                      <time className="tabular-nums">
                        {DateHelper.format(
                          entry.datetime,
                          "dd/MM/yyyy HH:mm:ss.SSS"
                        )}
                      </time>
                    </div>
                  </div>
                </div>
              </li>
            )
          })}
        </ol>
      </div>
    </div>
  )
}

// Botão "Reenviar" do header — habilitado só quando o envio pode
// ser reprocessado (FAILED/CANCELED); nos demais estados fica
// desabilitado com tooltip explicando (padrão do repo: tooltip em
// ação indisponível). Confirmação via AlertDialog → write otimista
// (badge muda p/ "Em fila" + evento no topo da timeline).
const ResendButton = ({
  schedulerId,
  channel,
}: {
  schedulerId: string
  channel: NotificationDetailChannelType
}) => {
  const { t } = useTranslation()
  const [confirmOpen, setConfirmOpen] = useState(false)
  const resendMutation = useResendNotification(
    schedulerId,
    channel.id
  )
  const canResend =
    channel.macroStatus === "FAILED" ||
    channel.macroStatus === "CANCELED"

  const button = (
    <Button
      type="button"
      variant="outline"
      size="sm"
      className="gap-1.5"
      disabled={!canResend || resendMutation.isPending}
      onClick={() => setConfirmOpen(true)}
    >
      <RotateCcwIcon size={14} aria-hidden="true" />
      {t(`${detailPrefix}.resend.button`)}
    </Button>
  )

  return (
    <>
      {canResend ? (
        button
      ) : (
        <TooltipProvider>
          <Tooltip>
            {/* span: botão desabilitado não dispara eventos de
                pointer — o wrapper garante o tooltip. */}
            <TooltipTrigger asChild>
              <span className="inline-flex">{button}</span>
            </TooltipTrigger>
            <TooltipContent>
              {t(`${detailPrefix}.resend.disabledTooltip`)}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}

      <AlertDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t(`${detailPrefix}.resend.dialogTitle`)}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t(
                `${detailPrefix}.resend.dialogDescription`
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              {t(`${detailPrefix}.resend.cancel`)}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => resendMutation.mutate()}
            >
              {t(`${detailPrefix}.resend.confirm`)}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}

// Card "Análise técnica": IDs copiáveis e números do envio — ouro
// para abrir ticket/troubleshooting sem caçar dado em log.
const TechnicalAnalysisCard = ({
  detail,
  channel,
}: {
  detail: NotificationDetailType
  channel: NotificationDetailChannelType
}) => {
  const { t, i18n } = useTranslation()
  // providerMessageId vem da timeline DO CANAL (evento SENT) — pega o
  // mais recente que o carrega.
  const providerMessageId = [...channel.timeline]
    .sort((a, b) => b.seq - a.seq)
    .find((entry) => entry.detail?.providerMessageId)
    ?.detail?.providerMessageId
  // Atraso entre a data programada do envio e a criação do primeiro
  // evento (relevante para envios agendados).
  const schedulingDelayMs = detail.dataInformation
    ? new Date(detail.dataInformation.createdAt).getTime() -
      new Date(detail.notificationDate).getTime()
    : null

  return (
    <SectionCard
      title={t(`${detailPrefix}.technicalAnalysis`)}
      icon={<WrenchIcon size={16} />}
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <InfoItem
          label={t(`${detailPrefix}.notificationUuid`)}
          value={channel.id}
          copyable
        />
        <InfoItem
          label={t(`${detailPrefix}.templateUsed`)}
          value={`${channel.template.id} @v${channel.template.version}`}
          copyable
        />
        {/* Datas do target: criação do registro e última transição. O
            agendamento (SCHED) NÃO é repetido aqui — já está no cabeçalho. */}
        <InfoItem
          label={t(`${detailPrefix}.createdAtLabel`)}
          value={DateHelper.format(
            channel.createdAt,
            "dd/MM/yyyy HH:mm:ss"
          )}
        />
        {channel.updatedAt && (
          <InfoItem
            label={t(`${detailPrefix}.updatedAtLabel`)}
            value={DateHelper.format(
              channel.updatedAt,
              "dd/MM/yyyy HH:mm:ss"
            )}
          />
        )}
        {providerMessageId && (
          <InfoItem
            label={t(`${detailPrefix}.providerMessageId`)}
            value={providerMessageId}
            copyable
          />
        )}
        {detail.dataInformation && (
          <>
            <InfoItem
              label={t(`${detailPrefix}.payloadVersion`)}
              value={`v${detail.dataInformation.templateVersion}`}
            />
            <InfoItem
              label={t(`${detailPrefix}.payloadSize`)}
              value={formatNotificationBytes(
                detail.dataInformation.sizeBytes,
                i18n.language
              )}
            />
          </>
        )}
        {schedulingDelayMs !== null && (
          <InfoItem
            label={t(`${detailPrefix}.schedulingDelay`)}
            value={formatNotificationDuration(
              schedulingDelayMs,
              i18n.language
            )}
          />
        )}
      </div>
    </SectionCard>
  )
}

// Painel de UM canal (NTGT) do envio — renderiza SÓ o que muda por
// canal (o que é comum ao envio fica fixo fora das abas): bloco de erro
// (quando FAILED) com reenvio contextual, bloco "Destinatário",
// timeline analítica, Análise técnica e o conteúdo efetivamente
// enviado. Lê o canal DIRETO de channels[i] do payload único (sem mais
// fetch por canal); os dados comuns do envio vêm de `detail`.
const ChannelPanel = ({
  detail,
  channel,
}: {
  detail: NotificationDetailType
  channel: NotificationDetailChannelType
}) => {
  const { t } = useTranslation()

  return (
    <div className="space-y-4 py-2">
      {channel.macroStatus === "FAILED" &&
        channel.error && (
          <div
            role="alert"
            className="rounded-md border border-red-200 bg-red-50 p-3"
          >
            <p className="text-sm font-medium text-red-700">
              {t(`${detailPrefix}.errorTitle`)}:{" "}
              {channel.error.reason}
            </p>
            <p className="text-xs text-red-700">
              {channel.error.detail}
            </p>
            {/* Ação de reenvio contextual: aparece SÓ na falha, junto
              do erro do canal. */}
            <div className="mt-3">
              <ResendButton
                schedulerId={detail.schedulerId}
                channel={channel}
              />
            </div>
          </div>
        )}

      {/* Bloco ÚNICO "Destinatário" (sem sub-abas): infos do destinatário
          do canal (Canal/Destinatário/Provedor/Status) + o template do
          canal (nome@versão) e o link "Ver template" DESTACADO. Fica ANTES
          da análise técnica. */}
      <SectionCard title={t(`${detailPrefix}.recipient`)}>
        <div className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            {/* Canal e Status são badges → label + badge (InfoItem só
                aceita string); Destinatário e Provedor via InfoItem. */}
            <div className="flex min-w-0 flex-col gap-1">
              <span className="text-xs font-medium text-muted-foreground">
                {t(`${detailPrefix}.channel`)}
              </span>
              <div>
                <NotificationChannelBadge
                  channel={channel.channel}
                />
              </div>
            </div>
            <InfoItem
              label={t(`${detailPrefix}.recipient`)}
              value={channel.recipient}
            />
            <InfoItem
              label={t(`${detailPrefix}.provider`)}
              value={channel.provider}
            />
            <div className="flex min-w-0 flex-col gap-1">
              <span className="text-xs font-medium text-muted-foreground">
                {t(`${detailPrefix}.statusLabel`)}
              </span>
              <div>
                <NotificationMacroStatusBadge
                  macroStatus={channel.macroStatus}
                  label={t(
                    `pages.notificationCenter.searchPage.macroStatus.${channel.macroStatus}`
                  )}
                />
              </div>
            </div>
            {/* Template do canal: nome@versão. */}
            <InfoItem
              label={t(`${detailPrefix}.templateName`)}
              value={`${channel.template.name} @v${channel.template.version}`}
            />
          </div>
          {/* Link DESTACADO para o template (ação primária, não texto
              discreto). O whiteLabelId é do ENVIO (comum). */}
          <div className="flex flex-wrap gap-2">
            <Link
              to={`/notification-center/configurations/${detail.whiteLabelId}/template-configuration/${channel.template.id}`}
              className={cn(
                buttonVariants({
                  variant: "default",
                  size: "sm",
                }),
                "gap-1.5"
              )}
            >
              <ExternalLinkIcon
                size={14}
                aria-hidden="true"
              />
              {t(`${detailPrefix}.templateLink`)}
            </Link>
          </div>
        </div>
      </SectionCard>

      <div className="grid gap-4 lg:grid-cols-2">
        {/* Timeline à direita; análise técnica à esquerda. */}
        <div className="lg:order-2">
          <SectionCard
            title={t(`${detailPrefix}.timeline`)}
            icon={<ClockIcon size={16} />}
          >
            <NotificationTimeline
              timeline={channel.timeline}
            />
          </SectionCard>
        </div>

        <div className="space-y-4 lg:order-1">
          <TechnicalAnalysisCard
            detail={detail}
            channel={channel}
          />
        </div>
      </div>

      {/* Conteúdo efetivamente enviado DESTE canal (sentTemplate singular). */}
      <NotificationSentTemplates
        sentTemplate={channel.sentTemplate}
        whiteLabelId={detail.whiteLabelId}
      />
    </div>
  )
}

export const NotificationDetail = ({
  uuid,
}: {
  // Id do ENVIO (scheduler, SCHED-…) vindo da rota.
  uuid: string
}) => {
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  // ?from= carrega os searchParams da Consulta de origem — o botão
  // "Voltar" restaura a busca exatamente como estava.
  const from = searchParams.get("from") ?? ""
  const backSearch = from.startsWith("?") ? from : ""
  // Detalhe do ENVIO (scheduler): um único payload com os dados COMUNS
  // (cabeçalho, origem, payload) e TODOS os canais em channels[].
  const {
    data: entry,
    error,
    isLoading,
    refetch,
  } = useNotificationCenterNotificationDetail(uuid)

  // Canais ABERTOS no acordeão (comportamento MULTI-open, espelhando o
  // request-detail do Heimdall: cada canal é um CARD colapsável
  // INDEPENDENTE — abrir um irmão NÃO fecha os demais). Início ENCOLHIDO
  // (nenhum canal aberto): o usuário abre o que quiser. O toggle GLOBAL
  // "Expandir/Encolher tudo" semeia todos de uma vez. Painel abre INLINE.
  const [openChannelIds, setOpenChannelIds] = useState<
    ReadonlySet<string>
  >(new Set())

  // Canais do envio, ORDENADOS por canal para não pularem entre renders.
  // Leitura DIRETA de channels[] do payload único — sem mais N+1.
  const channels = useMemo(
    () =>
      [...(entry?.channels ?? [])].sort((a, b) =>
        a.channel.localeCompare(b.channel)
      ),
    [entry]
  )

  // Alterna UM canal (add/remove do conjunto) — não mexe nos outros.
  const toggleChannel = (id: string) =>
    setOpenChannelIds((current) => {
      const next = new Set(current)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })

  // Estado do toggle GLOBAL: "todos abertos?" decide o rótulo/ícone e o
  // efeito (encolher tudo → conjunto vazio; expandir tudo → todos os ids).
  const allChannelsOpen =
    channels.length > 0 &&
    channels.every((channel) =>
      openChannelIds.has(channel.id)
    )
  const toggleAllChannels = () =>
    setOpenChannelIds(
      allChannelsOpen
        ? new Set()
        : new Set(channels.map((channel) => channel.id))
    )

  return (
    <div className="space-y-4 @container">
      <Button
        asChild
        variant="ghost"
        className="w-fit gap-1.5 px-2"
      >
        <Link
          to={`/notification-center/search${backSearch}`}
        >
          <ArrowLeftIcon
            className="size-4"
            aria-hidden="true"
          />
          {t(`${detailPrefix}.backButton`)}
        </Link>
      </Button>

      {isLoading && <DetailSkeleton />}
      {Boolean(error) && (
        <div className="flex flex-col items-center gap-3 rounded-md border border-red-200 bg-red-50 p-6">
          <p className="text-center text-sm text-red-700">
            {t(`${detailPrefix}.loadError`)}
          </p>
          <Button
            type="button"
            variant="outline"
            onClick={() => refetch()}
          >
            {t(`${detailPrefix}.retry`)}
          </Button>
        </div>
      )}
      {entry && (
        <>
          {/* Cabeçalho COMUM do envio (fixo): evento + tipo (agendado/
              imediato) + agendamento (SCHED) copiável + data. Canal/
              destinatário/status ficam POR CANAL (nas abas). */}
          <header className="flex flex-col gap-3">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-2xl font-bold tracking-tight">
                {entry.eventLabel}
              </h1>
              <Badge
                variant="outline"
                className="rounded-full font-medium"
              >
                {entry.scheduled
                  ? t(`${detailPrefix}.scheduledBadge`)
                  : t(`${detailPrefix}.immediateBadge`)}
              </Badge>
            </div>
          </header>

          {/* COMUM (fixo): Detalhes do ENVIO (notification_scheduler) —
              código SCHED, agendamento, criação, tipo (agendado/imediato) e
              a origem (transação/cliente). Igual para todos os canais, fica
              ACIMA das abas, não duplicado nelas. */}
          <SectionCard
            title={t(`${detailPrefix}.details`)}
            icon={<ReceiptTextIcon size={16} />}
          >
            <div className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <InfoItem
                  label={t(`${detailPrefix}.schedulerCode`)}
                  value={entry.schedulerId}
                  copyable
                />
                <InfoItem
                  label={t(`${detailPrefix}.dispatchType`)}
                  value={
                    entry.scheduled
                      ? t(`${detailPrefix}.scheduledBadge`)
                      : t(`${detailPrefix}.immediateBadge`)
                  }
                />
                <InfoItem
                  label={t(`${detailPrefix}.dispatchDate`)}
                  value={DateHelper.format(
                    entry.notificationDate,
                    "dd/MM/yyyy HH:mm:ss"
                  )}
                />
                <InfoItem
                  label={t(
                    `${detailPrefix}.createdAtLabel`
                  )}
                  value={
                    entry.createdAt
                      ? DateHelper.format(
                          entry.createdAt,
                          "dd/MM/yyyy HH:mm:ss"
                        )
                      : "—"
                  }
                />
                <InfoItem
                  label={t(`${detailPrefix}.transaction`)}
                  value={entry.origin.transactionId}
                  copyable
                />
                <InfoItem
                  label={t(`${detailPrefix}.customer`)}
                  value={
                    entry.origin.customerName ??
                    entry.origin.customerId
                  }
                  copyable
                />
              </div>
              <div className="flex flex-wrap gap-2">
                <ActionLinkButton
                  to={resolveOriginHref(entry)}
                >
                  {t(
                    `${detailPrefix}.originLink.${entry.origin.type}`
                  )}
                </ActionLinkButton>
                <ActionLinkButton
                  to={`/customers/details/${entry.origin.customerId}`}
                >
                  {t(`${detailPrefix}.customerLink`)}
                </ActionLinkButton>
              </div>
            </div>
          </SectionCard>

          {/* COMUM (fixo): Informações do envio (payload) é do ENVIO
              (notification_scheduler) — logo após a Origem e ANTES das
              abas de canal. */}
          <NotificationPayloadTree
            payload={entry.payload}
            sizeBytes={entry.dataInformation?.sizeBytes}
          />

          {/* Canais como ACORDEÃO MULTI-open (espelha o request-detail do
              Heimdall): cada canal é um CARD colapsável INDEPENDENTE —
              abrir um irmão NÃO fecha os outros. O painel do canal abre
              INLINE dentro do próprio card; um toggle GLOBAL "Expandir/
              Encolher tudo" semeia todos. Canal único → painel direto
              (sem lista). */}
          {channels.length > 1 ? (
            <div>
              {/* Cabeçalho da lista: legenda (N canais) + toggle GLOBAL de
                  expandir/encolher tudo (ChevronsUpDown/DownUp, como no
                  request-detail do Heimdall). */}
              <div className="mb-2 flex items-center justify-between gap-2">
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  {t(`${detailPrefix}.channelTabs.label`)} ·{" "}
                  {channels.length}
                </p>
                <button
                  type="button"
                  onClick={toggleAllChannels}
                  aria-pressed={allChannelsOpen}
                  className="inline-flex items-center gap-1.5 rounded-lg border bg-card px-2.5 py-1 text-xs font-medium text-muted-foreground shadow-sm transition-colors hover:bg-accent hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
                >
                  {allChannelsOpen ? (
                    <ChevronsDownUpIcon
                      size={14}
                      aria-hidden="true"
                    />
                  ) : (
                    <ChevronsUpDownIcon
                      size={14}
                      aria-hidden="true"
                    />
                  )}
                  {allChannelsOpen
                    ? t(
                        `${detailPrefix}.channelTabs.collapseAll`
                      )
                    : t(
                        `${detailPrefix}.channelTabs.expandAll`
                      )}
                </button>
              </div>
              {/* Cada canal é um CARD independente: header clicável (chevron
                  à ESQUERDA que gira 90° ao abrir) + painel INLINE dentro do
                  próprio card quando aberto. Acento à esquerda pela cor do
                  status (HEX inline — imune ao purge, igual à timeline). */}
              <div
                role="group"
                aria-label={t(
                  `${detailPrefix}.channelTabs.label`
                )}
                className="flex w-full flex-col gap-2"
              >
                {channels.map((channel) => {
                  const isOpen = openChannelIds.has(
                    channel.id
                  )
                  const isFailed =
                    channel.macroStatus === "FAILED"
                  return (
                    <article
                      key={channel.id}
                      style={{
                        borderLeftColor:
                          NOTIFICATION_MACRO_STATUS_CHART_COLORS[
                            channel.macroStatus
                          ],
                      }}
                      className={cn(
                        "overflow-hidden rounded-xl border border-l-4 bg-card shadow-sm transition-shadow hover:shadow-md motion-reduce:transition-none",
                        isFailed &&
                          "bg-red-500/[0.06] dark:bg-red-500/10"
                      )}
                    >
                      <button
                        type="button"
                        aria-expanded={isOpen}
                        aria-controls={`channel-panel-${channel.id}`}
                        onClick={() =>
                          toggleChannel(channel.id)
                        }
                        className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-accent/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring motion-reduce:transition-none"
                      >
                        <ChevronRightIcon
                          size={16}
                          aria-hidden="true"
                          className={cn(
                            "shrink-0 text-muted-foreground transition-transform motion-reduce:transition-none",
                            isOpen && "rotate-90"
                          )}
                        />
                        <span className="flex flex-wrap items-center gap-2">
                          <NotificationChannelBadge
                            channel={channel.channel}
                          />
                          <NotificationMacroStatusBadge
                            macroStatus={
                              channel.macroStatus
                            }
                            label={t(
                              `pages.notificationCenter.searchPage.macroStatus.${channel.macroStatus}`
                            )}
                          />
                          <span className="text-xs text-muted-foreground">
                            {channel.recipient}
                          </span>
                        </span>
                      </button>
                      {/* Painel do canal INLINE, dentro do card, ao abrir. */}
                      {isOpen && (
                        <div
                          id={`channel-panel-${channel.id}`}
                          className="border-t px-4 pb-2"
                        >
                          <ChannelPanel
                            detail={entry}
                            channel={channel}
                          />
                        </div>
                      )}
                    </article>
                  )
                })}
              </div>
            </div>
          ) : (
            channels.length === 1 && (
              <ChannelPanel
                detail={entry}
                channel={channels[0]}
              />
            )
          )}
        </>
      )}
    </div>
  )
}
